import moment from 'moment-timezone';
import Component from '@glimmer/component';
import { ISkuReplacementInfo } from 'volta/models/types/sku';
import { cached } from '@glimmer/tracking';
import { inject as service } from '@ember/service';
import IntlService from 'ember-intl/services/intl';

interface IArgs {
  replacedBy?: ISkuReplacementInfo[];
  replacing?: ISkuReplacementInfo[];
  daysBeforeAlert?: number;
}

const MAX_DIFF = 8;

const getReplacementDate = (replacement: ISkuReplacementInfo[]) => {
  return replacement.reduce((acc, r) => {
    if (r.replaceOn && !acc) {
      acc = r.replaceOn;
    }

    return acc;
  }, undefined as Date | undefined);
};

const getReplacementDateDiff = (date: Date) => {
  const m = moment(date);
  return m.diff(moment(), 'd');
};

const getReplacementColor = (diff: number, daysBeforeAlert: number) => {
  if (diff > 0 && diff <= daysBeforeAlert) {
    return 'warning';
  } else return 'info';
};

const getReplacementDetails = (
  replacement: ISkuReplacementInfo[] | undefined,
  daysBeforeAlert: number
) => {
  if (!replacement || !replacement.length) {
    return undefined;
  }
  const date = getReplacementDate(replacement);
  const diff = date ? getReplacementDateDiff(date) : undefined;
  const color = diff ? getReplacementColor(diff, daysBeforeAlert) : undefined;
  return {
    count: replacement.length,
    date,
    diff,
    color,
    dateText: date ? moment(date).format('L') : undefined,
    replacement
  };
};

export default class SkuReplacementStatus extends Component<IArgs> {
  @service intl!: IntlService;

  get daysBeforeAlert() {
    return Math.max(this.args.daysBeforeAlert ?? MAX_DIFF, MAX_DIFF);
  }

  @cached
  get replacedDetails() {
    return getReplacementDetails(this.args.replacedBy ?? [], this.daysBeforeAlert);
  }

  @cached
  get replacingDetails() {
    return getReplacementDetails(this.args.replacing ?? [], this.daysBeforeAlert);
  }

  get replacedText() {
    const details = this.replacedDetails;
    if (!details) return undefined;

    const attrs = {
      date: details.dateText,
      count: details.count
    };

    if (!details.date) {
      return this.intl.t('skuReplacement.isReplacedBy', attrs);
    }

    return (details.diff as number) < 0
      ? this.intl.t('skuReplacement.hasBeenReplaced', attrs)
      : this.intl.t('skuReplacement.willBeReplacedOn', attrs);
  }

  get replacingText() {
    const details = this.replacingDetails;
    if (!details) return undefined;

    const attrs = {
      date: details.dateText,
      count: details.count
    };

    if (!details.date) {
      return this.intl.t('skuReplacement.isReplacing', attrs);
    }

    return (details.diff as number) < 0
      ? this.intl.t('skuReplacement.hasReplaced', attrs)
      : this.intl.t('skuReplacement.willReplaceOn', attrs);
  }
}
